import { useEffect, useState } from "react";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { deleteShopForVehicle, pushShopForVehicle } from "./shop-for-vehicle-api";

export type ShopForVehicleSelection = {
  id: string;
  title: string;
  subtitle: string | null;
  vehicleTypeId: string | null;
  makeId: string;
  modelId: string;
  variantId: string | null;
  year: number | null;
  userVehicleId: string | null;
};

type ShopForVehicleState = {
  selection: ShopForVehicleSelection | null;
  setShopForVehicle: (selection: ShopForVehicleSelection | null) => void;
  clear: () => void;
  hydrateFromServer: (selection: ShopForVehicleSelection | null) => void;
  resetLocal: () => void;
};

export const useShopForVehicle = create<ShopForVehicleState>()(
  persist(
    (set) => ({
      selection: null,
      setShopForVehicle: (selection) => {
        set({ selection });
        if (selection === null) {
          void deleteShopForVehicle().catch(() => {});
        } else {
          void pushShopForVehicle(selection).catch(() => {});
        }
      },
      clear: () => {
        set({ selection: null });
        void deleteShopForVehicle().catch(() => {});
      },
      hydrateFromServer: (selection) => set({ selection }),
      resetLocal: () => set({ selection: null }),
    }),
    {
      name: "sparecart.shopForVehicle",
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ selection: state.selection }),
    },
  ),
);

export const useShopForVehicleHydrated = (): boolean => {
  const [hydrated, setHydrated] = useState(() => useShopForVehicle.persist.hasHydrated());

  useEffect(() => {
    if (hydrated) return undefined;
    return useShopForVehicle.persist.onFinishHydration(() => setHydrated(true));
  }, [hydrated]);

  return hydrated;
};

export const selectShopForVehicle = (state: ShopForVehicleState): ShopForVehicleSelection | null =>
  state.selection;

export const selectShopForVehicleId = (state: ShopForVehicleState): string | null =>
  state.selection?.id ?? null;

export const selectSetShopForVehicle = (
  state: ShopForVehicleState,
): ((selection: ShopForVehicleSelection | null) => void) => state.setShopForVehicle;

export const selectClearShopForVehicle = (state: ShopForVehicleState): (() => void) => state.clear;

export const selectHydrateShopForVehicle = (
  state: ShopForVehicleState,
): ((selection: ShopForVehicleSelection | null) => void) => state.hydrateFromServer;

export const selectResetLocalShopForVehicle = (state: ShopForVehicleState): (() => void) =>
  state.resetLocal;
